// One live connection per open project: the Yjs document, its provider, presence and undo.
import { HocuspocusProvider } from '@hocuspocus/provider';
import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import { ROOT, type Actor } from '../../shared/doc.js';
import { LOCAL } from '../../shared/ops.js';
import type { Me } from '../api.js';

export type Problem = null | 'signed-out' | 'no-access';
export interface ProjectConn {
  doc: Y.Doc;
  provider: HocuspocusProvider;
  undo: Y.UndoManager;
  me: Actor;
  colour: string;
  status: 'connecting' | 'connected' | 'disconnected';
  synced: boolean;
  problem: Problem;
}

const COLOURS = ['#e11d48', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4d7c0f'];
export function colourFor(userId: string) {
  let h = 0;
  for (const c of userId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLOURS[h % COLOURS.length];
}

export function useProject(projectId: string, user: Me): ProjectConn | null {
  const [conn, setConn] = useState<ProjectConn | null>(null);

  useEffect(() => {
    const doc = new Y.Doc();
    const me: Actor = { kind: 'human', userId: user.id, name: user.name };
    const colour = colourFor(user.id);
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/sync`;
    let state: ProjectConn;
    const update = (patch: Partial<ProjectConn>) => {
      state = { ...state, ...patch };
      setConn(state);
    };
    const provider = new HocuspocusProvider({
      url,
      name: projectId,
      document: doc,
      token: 'session', // the server authenticates with the session cookie
      onStatus: ({ status }) => update({ status }),
      onSynced: () => update({ synced: true }),
      onAuthenticationFailed: () => {
        update({ problem: 'no-access' });
        provider.disconnect();
      },
      onClose: ({ event }) => {
        if (event.code === 4401) {
          update({ problem: 'signed-out' });
          provider.disconnect();
        } else if (event.code === 4403) {
          update({ problem: 'no-access' });
        }
      },
    });
    provider.awareness?.setLocalStateField('user', { id: user.id, name: user.name, colour });
    const undo = new Y.UndoManager([doc.getMap(ROOT.cards), doc.getMap(ROOT.boards), doc.getArray(ROOT.spark), doc.getMap(ROOT.routes), doc.getMap(ROOT.meta)], {
      trackedOrigins: new Set([LOCAL]),
      captureTimeout: 400,
    });
    state = { doc, provider, undo, me, colour, status: 'connecting', synced: false, problem: null };
    setConn(state);
    if (localStorage.getItem('planaDebug') === '1') {
      (window as unknown as { __plana: unknown }).__plana = { doc, provider, disconnect: () => provider.disconnect(), connect: () => provider.connect() };
    }
    return () => {
      undo.destroy();
      provider.destroy();
      doc.destroy();
    };
  }, [projectId, user.id, user.name]);

  return conn;
}
